import { useCallback, useEffect, useMemo } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  MarkerType,
  useReactFlow,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from '@dagrejs/dagre'
import { OrionFlowNode, type OrionFlowNodeType } from '../OrionFlowNode'
import { OrionFlowEdge } from '../OrionFlowEdge'
import type { OntoNodeKind, OntoPathNode } from '../../types'

const nodeTypes = { orion: OrionFlowNode }
const edgeTypes = { orion: OrionFlowEdge }

const ESTIMATED_NODE_WIDTH = 190
const ESTIMATED_NODE_HEIGHT = 52
const STAGGER_DEPTH = 0.18

const edgeStyleIdle: React.CSSProperties = {
  stroke: 'oklch(75% 0.16 65 / 0.7)',
  strokeWidth: 1.5,
  strokeDasharray: '4 4',
}
const edgeStyleOn: React.CSSProperties = {
  stroke: 'oklch(75% 0.16 65 / 1)',
  strokeWidth: 2.2,
}
const edgeStyleOff: React.CSSProperties = {
  stroke: 'oklch(58% 0.03 250 / 0.2)',
  strokeWidth: 1,
  strokeDasharray: '3 4',
}
const markerEndIdle = { type: MarkerType.ArrowClosed, color: 'oklch(75% 0.16 65)', width: 16, height: 16 }
const markerEndOn = { type: MarkerType.ArrowClosed, color: 'oklch(75% 0.16 65)', width: 18, height: 18 }
const markerEndOff = { type: MarkerType.ArrowClosed, color: 'oklch(58% 0.03 250 / 0.4)', width: 14, height: 14 }

interface LayoutResult {
  nodes: OrionFlowNodeType[]
  graphHeight: number
}

function layoutGraph(rfNodes: OrionFlowNodeType[], edges: Edge[]): LayoutResult {
  const g = new dagre.graphlib.Graph()
  g.setGraph({ rankdir: 'LR', ranksep: 55, nodesep: 18, edgesep: 12, marginx: 12, marginy: 12 })
  g.setDefaultEdgeLabel(() => ({}))
  rfNodes.forEach((n) => g.setNode(n.id, { width: ESTIMATED_NODE_WIDTH, height: ESTIMATED_NODE_HEIGHT }))
  edges.forEach((e) => g.setEdge(e.source, e.target))
  dagre.layout(g)
  const { height: graphHeight } = g.graph() as { width: number; height: number }
  const laid = rfNodes.map((n) => {
    const pos = g.node(n.id)
    return {
      ...n,
      position: { x: pos.x - ESTIMATED_NODE_WIDTH / 2, y: pos.y - ESTIMATED_NODE_HEIGHT / 2 },
    }
  })
  return { nodes: laid, graphHeight }
}

function computeAncestors(path: OntoPathNode[], rootId: string): Set<string> {
  const byId: Record<string, OntoPathNode> = {}
  path.forEach((p) => {
    byId[p.id] = p
  })
  const ancestors = new Set<string>()
  const stack = [rootId]
  while (stack.length > 0) {
    const id = stack.pop()!
    if (ancestors.has(id)) continue
    ancestors.add(id)
    const node = byId[id]
    node?.parents?.forEach((pid) => stack.push(pid))
  }
  return ancestors
}

function computeDepths(path: OntoPathNode[]): Record<string, number> {
  const byId: Record<string, OntoPathNode> = {}
  path.forEach((p) => {
    byId[p.id] = p
  })
  const depths: Record<string, number> = {}
  function dfs(id: string): number {
    if (depths[id] !== undefined) return depths[id]
    const n = byId[id]
    if (!n || !n.parents || n.parents.length === 0) {
      depths[id] = 0
      return 0
    }
    const d = Math.max(...n.parents.map((p) => dfs(p))) + 1
    depths[id] = d
    return d
  }
  path.forEach((n) => dfs(n.id))
  return depths
}

export interface LabFlowProps {
  path: OntoPathNode[]
  kindLabels: Record<OntoNodeKind, string>
  attributionRootId?: string | null
  expandedNodeId?: string | null
  pathKey?: string
  onNodeClick?: (id: string) => void
  onPaneClick?: () => void
  minHeight?: number
  maxHeight?: number
}

export function LabFlow(props: LabFlowProps) {
  return (
    <ReactFlowProvider>
      <LabFlowInner {...props} />
    </ReactFlowProvider>
  )
}

function LabFlowInner({
  path,
  kindLabels,
  attributionRootId,
  expandedNodeId,
  pathKey = 'default',
  onNodeClick,
  onPaneClick,
  minHeight = 240,
  maxHeight = 360,
}: LabFlowProps) {
  const reactFlow = useReactFlow()

  const ancestorSet = useMemo(() => {
    if (!attributionRootId) return null
    return computeAncestors(path, attributionRootId)
  }, [path, attributionRootId])

  const depths = useMemo(() => computeDepths(path), [path])

  const { nodes, edges, boardHeight } = useMemo(() => {
    const rfNodes: OrionFlowNodeType[] = path.map((p) => {
      const depth = depths[p.id] ?? 0
      const attribution: 'on' | 'off' | 'idle' = !ancestorSet
        ? 'idle'
        : ancestorSet.has(p.id)
          ? 'on'
          : 'off'
      return {
        id: p.id,
        type: 'orion',
        position: { x: 0, y: 0 },
        data: {
          kind: p.kind,
          kindLabel: kindLabels[p.kind],
          label: p.label,
          detail: p.detail,
          citation: p.citation,
          expanded: expandedNodeId === p.id,
          appearKey: `${pathKey}::${p.id}::${ancestorSet ? 'attr' : 'idle'}`,
          appearDelay: ancestorSet ? 0 : depth * STAGGER_DEPTH,
          attribution,
        },
      }
    })

    const rfEdges: Edge[] = []
    path.forEach((p) => {
      const childDepth = depths[p.id] ?? 0
      ;(p.parents ?? []).forEach((pid) => {
        const edgeAttr: 'on' | 'off' | 'idle' = !ancestorSet
          ? 'idle'
          : ancestorSet.has(pid) && ancestorSet.has(p.id)
            ? 'on'
            : 'off'
        const style = edgeAttr === 'on' ? edgeStyleOn : edgeAttr === 'off' ? edgeStyleOff : edgeStyleIdle
        const marker = edgeAttr === 'on' ? markerEndOn : edgeAttr === 'off' ? markerEndOff : markerEndIdle
        rfEdges.push({
          id: `${pid}->${p.id}`,
          source: pid,
          target: p.id,
          type: 'orion',
          animated: false,
          style,
          markerEnd: marker,
          data: {
            appearKey: `${pathKey}::${pid}->${p.id}::${ancestorSet ? 'attr' : 'idle'}`,
            appearDelay: ancestorSet ? 0 : Math.max(0, childDepth * STAGGER_DEPTH - 0.05),
          },
        })
      })
    })

    const layout = layoutGraph(rfNodes, rfEdges)
    const computedHeight = Math.max(minHeight, Math.min(maxHeight, Math.round(layout.graphHeight + 60)))
    return { nodes: layout.nodes, edges: rfEdges, boardHeight: computedHeight }
  }, [path, depths, ancestorSet, expandedNodeId, pathKey, kindLabels, minHeight, maxHeight])

  const topologyKey = useMemo(() => path.map((n) => n.id).sort().join('|'), [path])
  useEffect(() => {
    const ti = window.setTimeout(() => {
      reactFlow.fitView({ padding: 0.08, duration: 400, maxZoom: 1.1, minZoom: 0.55 })
    }, 30)
    return () => window.clearTimeout(ti)
  }, [topologyKey, pathKey, reactFlow])

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      onNodeClick?.(node.id)
    },
    [onNodeClick],
  )

  return (
    <div
      className="relative bg-[color:var(--color-orion-deep)]/30 rounded-[var(--radius-md)] border-faint overflow-hidden transition-[height] duration-300"
      style={{ height: boardHeight }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.08, maxZoom: 1.1, minZoom: 0.55 }}
        proOptions={{ hideAttribution: true }}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        zoomOnDoubleClick={false}
        minZoom={0.3}
        maxZoom={2.5}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        defaultEdgeOptions={{ type: 'orion' }}
        onPaneClick={onPaneClick}
        onNodeClick={handleNodeClick}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="oklch(58% 0.03 250 / 0.32)" />
      </ReactFlow>
      <FlowControls />
    </div>
  )
}

function FlowControls() {
  const rf = useReactFlow()
  const btnClass =
    'w-7 h-7 flex items-center justify-center rounded-[var(--radius-sm)] mono text-[12px] ' +
    'bg-[color:var(--color-orion-night)]/90 border-faint backdrop-blur-sm ' +
    'text-[color:var(--color-orion-text)] hover:bg-[color:var(--color-orion-amber)]/20 ' +
    'hover:border-[color:var(--color-orion-amber)]/60 transition-colors'
  return (
    <div className="absolute bottom-3 right-3 z-10 flex flex-col gap-1">
      <button type="button" onClick={() => rf.zoomIn({ duration: 200 })} aria-label="zoom in" className={btnClass}>
        +
      </button>
      <button type="button" onClick={() => rf.zoomOut({ duration: 200 })} aria-label="zoom out" className={btnClass}>
        −
      </button>
      <button
        type="button"
        onClick={() => rf.fitView({ padding: 0.08, duration: 400, maxZoom: 1.1, minZoom: 0.55 })}
        aria-label="fit view"
        className={btnClass}
      >
        ⤢
      </button>
    </div>
  )
}
